import { Divider, Modal } from "antd";
import { useMemo, useState } from "react";
import { useIntl } from "react-intl";
import PermissionCheck from "~Common/PermissionCheck";
import { UploadImageForm } from "~components/Images";
import { useUserPermissions } from "~context/UserPermissionsContext";
import { PermissionNames, PermissionOperations } from "~enums/permissions";
import type { Image } from "~types/image.ts";
import "./LogoUploadModal.scss";
import { ImageBaseTypes } from "~enums/imageBaseTypes";
import { ImageSizes } from "~enums/imageSizes.ts";
import { ImageTypes } from "~enums/imageTypes";
import { findImageByTypes } from "~utilities/findImageByTypes.ts";
import ImageView from "../../../../components/ImageView/ImageView.tsx";

type Props = {
  images: Image[];
  organizationId: string;
  refetch: () => void;
};

export const LogoUploadModal = ({ images, organizationId, refetch }: Props) => {
  const [open, setOpen] = useState(false);
  const { formatMessage } = useIntl();
  const { hasPermission } = useUserPermissions();
  const hasEditPermission = hasPermission(PermissionNames.ORGANIZATION, PermissionOperations.UPDATE);
  const uploadButtonClickHandler = useMemo(() => (hasEditPermission ? () => setOpen(true) : undefined), [hasEditPermission]);

  const currentLogo = findImageByTypes(images, [ImageTypes.Logo]);

  return (
    <>
      <Modal
        title={
          <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
            <i className={`fas fa-camera`} />
            {formatMessage({ id: "upsert.organization.logo.modal.title" })}
          </div>
        }
        open={open}
        width={350}
        okButtonProps={{ hidden: true }}
        cancelButtonProps={{ hidden: true }}
        onCancel={() => {
          setOpen(false);
        }}
        afterClose={refetch}
      >
        <div>{formatMessage({ id: "upsert.organization.logo.modal.description" })}</div>
        <Divider style={{ margin: "5px 0" }} />
        <UploadImageForm
          imageType={ImageTypes.Logo}
          baseId={organizationId}
          images={images}
          baseType={ImageBaseTypes.Organization}
          permissionKey={PermissionNames.ORGANIZATION}
        />
      </Modal>
      <div className="image-upload-modal-button" onClick={uploadButtonClickHandler}>
        <ImageView
          className="logo-preview"
          imageUrl={currentLogo?.url}
          imageType={ImageTypes.Logo}
          size={ImageSizes.Small}
          width={80}
          height={80}
          fallback={
            <div className="upload-logo-preview">
              <div className="upload-logo-text">{formatMessage({ id: "upload.logo" })}</div>
              <div className="upload-logo-icon">
                <i className={`fas fa-2x fa-shield-halved`} />
              </div>
            </div>
          }
        />
        <PermissionCheck permissionKeys={[PermissionNames.ORGANIZATION]} permissionOptions={[PermissionOperations.UPDATE]}>
          <div className="camera-icon">
            <i className={`fa-solid fa-2xs fa-camera`} />
          </div>
        </PermissionCheck>
      </div>
    </>
  );
};
